import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  crmAnalyticsApi,
  type DateRangeParams,
  type PrescreeningCriteriaFailureItem
} from "api/crm-analytics";
import { getErrorMessage } from "utils/error";
import { AnalyticsDetailModal } from "./AnalyticsDetailModal";
import { PropertyGroupHeader } from "./PropertyGroupHeader";
import type { UnitFilterParams } from "./UnitsFilter";
import {
  DEFAULT_PER_PAGE,
  DRILLDOWN_ROWS_BASE_CLASSNAME,
  THREE_COLS_DRILLDOWN_GRID_CLASSNAME
} from "../constants";
import { groupByProperty } from "../utils";
import { cx } from "shared/utils";

const COLUMNS = [
  { label: "Lead name" },
  { label: "Unit" },
  { label: "Request date", className: "text-right" }
];

const ROW_TEXT_CLASSNAME = "text-sm font-medium text-content-700 truncate";

const PLACEHOLDER = "--";

const FailureRow = ({ item }: { item: PrescreeningCriteriaFailureItem }) => {
  const requestedAt = moment(item.requested_at);

  return (
    <Link
      to={`/leads/${item.lead_id}`}
      className={cx(
        THREE_COLS_DRILLDOWN_GRID_CLASSNAME,
        DRILLDOWN_ROWS_BASE_CLASSNAME
      )}
    >
      <p className={ROW_TEXT_CLASSNAME}>{item.lead_name ?? PLACEHOLDER}</p>
      <p className={ROW_TEXT_CLASSNAME}>{item.unit_title ?? PLACEHOLDER}</p>
      <div className="text-right">
        <p className={cx(ROW_TEXT_CLASSNAME, "whitespace-nowrap")}>
          {requestedAt.format("MMMM D, YYYY")}
        </p>
        <p className={ROW_TEXT_CLASSNAME}>{requestedAt.format("h:mm A")}</p>
      </div>
    </Link>
  );
};

const selectFailureProperty = (item: PrescreeningCriteriaFailureItem) => ({
  id: null,
  name: item.property_name
});

export const PrescreeningFailuresModal = ({
  opened,
  onClose,
  dateParams,
  filterParams
}: {
  opened: boolean;
  onClose: () => void;
  dateParams: DateRangeParams;
  filterParams?: UnitFilterParams;
}) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (opened) setPage(1);
  }, [opened]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "CRM_ANALYTICS_PRESCREENING_FAILURES",
      dateParams,
      page,
      filterParams
    ],
    queryFn: () =>
      crmAnalyticsApi.getPrescreeningFailures({
        ...filterParams,
        ...dateParams,
        page,
        per_page: Number(DEFAULT_PER_PAGE)
      }),
    enabled: opened,
    placeholderData: keepPreviousData,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const totalPages = data?.page_count ?? 0;
  const pagination = useMemo(
    () => ({ total: totalPages, current: page, onChange: setPage }),
    [totalPages, page]
  );
  const groups = useMemo(
    () => groupByProperty(items, selectFailureProperty),
    [items]
  );

  return (
    <AnalyticsDetailModal
      opened={opened}
      onClose={onClose}
      title="Pre-screening criteria failures"
      gridClassName={THREE_COLS_DRILLDOWN_GRID_CLASSNAME}
      columns={COLUMNS}
      isLoading={isLoading}
      isFetching={isFetching}
      isEmpty={items.length === 0}
      emptyText="No pre-screening criteria failures found"
      pagination={pagination}
    >
      {groups.map((group) => (
        <Fragment key={group.key}>
          <PropertyGroupHeader name={group.name} />
          {group.items.map((item, index) => (
            <FailureRow
              key={`${item.lead_id}-${item.requested_at}-${index}`}
              item={item}
            />
          ))}
        </Fragment>
      ))}
    </AnalyticsDetailModal>
  );
};
